import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Pause, 
  Play, 
  ChevronLeft, 
  ChevronRight,
  AlertCircle,
  Trophy,
  BookOpen,
  Target,
  BarChart3,
  History,
  GraduationCap,
  Brain,
  Lightbulb,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NCLEXPractice = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token'));
  
  // Tab and view state
  const [selectedExamType, setSelectedExamType] = useState('lpn');
  const [mode, setMode] = useState('test'); // 'test' or 'study'
  const [view, setView] = useState('home'); // 'home', 'test', 'study', 'results', 'study-results'
  
  // Test state
  const [testId, setTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Study mode state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [studyFeedback, setStudyFeedback] = useState(null);
  const [studyResults, setStudyResults] = useState({});
  
  // Results state
  const [results, setResults] = useState(null);
  
  // History state
  const [testHistory, setTestHistory] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Timer ref
  const timerRef = useRef(null);
  const latestExamType = useRef(selectedExamType);

  // Check auth
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  // Timer logic
  useEffect(() => {
    if (view === 'test' && !isPaused && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - auto submit
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [view, isPaused, timeRemaining]);

  // Fetch history and stats
  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nclex/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTestHistory(data.tests || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nclex/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (token && view === 'home') {
      fetchHistory();
      fetchStats();
      fetchCategories();
    }
  }, [token, view, selectedExamType]);

  // Fetch categories for study mode
  latestExamType.current = selectedExamType;
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nclex/categories/${selectedExamType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Ignore stale responses if the exam type changed while this request was in flight
        if (data.exam_type === latestExamType.current) setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Start a study session
  const handleStartStudy = async () => {
    if (!selectedCategory) {
      alert('Please select a category to study');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nclex/study/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          exam_type: selectedExamType,
          category: selectedCategory,
          num_questions: 20
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to start study session');
      }
      
      const data = await response.json();
      setTestId(data.session_id);
      setQuestions(data.questions);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setStudyFeedback(null);
      setStudyResults({});
      setView('study');
    } catch (error) {
      console.error('Error starting study session:', error);
      alert('Failed to start study session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Check answer in study mode (immediate feedback)
  const handleCheckStudyAnswer = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestion.id];
    
    if (!currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0)) {
      alert('Please select an answer before checking');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nclex/study/${testId}/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question_id: currentQuestion.id,
          answer: currentAnswer
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to check answer');
      }
      
      const data = await response.json();
      setStudyFeedback(data);
      setStudyResults(prev => ({
        ...prev,
        [currentQuestion.id]: data
      }));
    } catch (error) {
      console.error('Error checking answer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Move to next question in study mode
  const handleNextStudyQuestion = () => {
    setStudyFeedback(null);
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  // Complete study session
  const handleCompleteStudy = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nclex/study/${testId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete study session');
      }
      
      const data = await response.json();
      setResults(data.score);
      setView('study-results');
    } catch (error) {
      console.error('Error completing study:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Start a new test
  const handleStartTest = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nclex/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ exam_type: selectedExamType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to start test');
      }
      
      const data = await response.json();
      setTestId(data.test_id);
      setQuestions(data.questions);
      setTimeRemaining(data.total_time);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setIsPaused(false);
      setView('test');
    } catch (error) {
      console.error('Error starting test:', error);
      alert('Failed to start test. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle answer selection
  const handleAnswerSelect = (questionId, answer, isMultiple = false) => {
    if (isMultiple) {
      // SATA question - toggle the answer
      const currentAnswers = answers[questionId] || [];
      let newAnswers;
      if (currentAnswers.includes(answer)) {
        newAnswers = currentAnswers.filter(a => a !== answer);
      } else {
        newAnswers = [...currentAnswers, answer];
      }
      setAnswers({ ...answers, [questionId]: newAnswers });
    } else {
      // Single answer
      setAnswers({ ...answers, [questionId]: answer });
    }
    
    // Save answer to backend
    fetch(`${API_URL}/api/nclex/test/${testId}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        question_id: questionId, 
        answer: isMultiple ? (answers[questionId] || []) : answer 
      })
    }).catch(err => console.error('Error saving answer:', err));
  };

  // Pause test
  const handlePauseTest = async () => {
    setIsPaused(true);
    try {
      await fetch(`${API_URL}/api/nclex/test/${testId}/pause?time_remaining=${timeRemaining}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error pausing test:', error);
    }
  };

  // Resume test
  const handleResumeTest = async () => {
    setIsPaused(false);
    try {
      await fetch(`${API_URL}/api/nclex/test/${testId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error resuming test:', error);
    }
  };

  // Submit test
  const handleSubmitTest = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nclex/test/${testId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ answers })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit test');
      }
      
      const data = await response.json();
      setResults(data.score);
      setView('results');
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Failed to submit test. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current question
  const currentQuestion = questions[currentQuestionIndex];

  // Render home view
  const renderHome = () => (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">NCLEX Practice</h1>
        <p className="text-slate-600">Master your nursing knowledge with practice tests and focused study</p>
      </div>

      {/* Exam Type Tabs */}
      <div className="flex justify-center mb-6">
        <div className="bg-slate-100 p-1 rounded-xl inline-flex" data-testid="exam-type-tabs">
          <button
            onClick={() => setSelectedExamType('lpn')}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              selectedExamType === 'lpn'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="lpn-tab"
          >
            <GraduationCap className="w-5 h-5 inline-block mr-2" />
            LPN/LVN
          </button>
          <button
            onClick={() => setSelectedExamType('rn')}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              selectedExamType === 'rn'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="rn-tab"
          >
            <GraduationCap className="w-5 h-5 inline-block mr-2" />
            RN
          </button>
        </div>
      </div>

      {/* Mode Selection Tabs */}
      <div className="flex justify-center mb-8">
        <div className="bg-slate-200 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setMode('test')}
            className={`px-6 py-2 rounded-md font-medium transition-all flex items-center ${
              mode === 'test'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="test-mode-tab"
          >
            <Clock className="w-4 h-4 mr-2" />
            Timed Test
          </button>
          <button
            onClick={() => setMode('study')}
            className={`px-6 py-2 rounded-md font-medium transition-all flex items-center ${
              mode === 'study'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="study-mode-tab"
          >
            <Brain className="w-4 h-4 mr-2" />
            Study Mode
          </button>
        </div>
      </div>

      {/* Test Mode Card */}
      {mode === 'test' && (
        <div className={`max-w-lg mx-auto mb-8 rounded-2xl p-6 shadow-lg ${
          selectedExamType === 'lpn' ? 'bg-blue-50 border-2 border-blue-200' : 'bg-emerald-50 border-2 border-emerald-200'
        }`}>
          <h2 className={`text-xl font-bold mb-4 ${
            selectedExamType === 'lpn' ? 'text-blue-800' : 'text-emerald-800'
          }`}>
            {selectedExamType === 'lpn' ? 'LPN/LVN' : 'RN'} Practice Test
          </h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center text-slate-700">
              <BookOpen className="w-5 h-5 mr-3 text-slate-500" />
              <span><strong>40 Questions</strong> randomly selected</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Clock className="w-5 h-5 mr-3 text-slate-500" />
              <span><strong>1.5 minutes</strong> per question (60 minutes total)</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Target className="w-5 h-5 mr-3 text-slate-500" />
              <span><strong>75%</strong> passing score</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Pause className="w-5 h-5 mr-3 text-slate-500" />
              <span><strong>Pause & Resume</strong> available</span>
            </div>
          </div>

          <button
            onClick={handleStartTest}
            disabled={isLoading}
            className={`w-full py-4 rounded-xl font-bold text-white text-lg transition-all ${
              selectedExamType === 'lpn'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-emerald-600 hover:bg-emerald-700'
            } disabled:opacity-50`}
            data-testid="start-test-btn"
          >
            {isLoading ? 'Starting...' : 'Start Practice Test'}
          </button>
        </div>
      )}

      {/* Study Mode Card */}
      {mode === 'study' && (
        <div className="max-w-lg mx-auto mb-8 rounded-2xl p-6 shadow-lg bg-purple-50 border-2 border-purple-200">
          <h2 className="text-xl font-bold mb-4 text-purple-800 flex items-center">
            <Brain className="w-6 h-6 mr-2" />
            Study Mode
          </h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center text-slate-700">
              <Lightbulb className="w-5 h-5 mr-3 text-purple-500" />
              <span><strong>No Timer</strong> - Learn at your own pace</span>
            </div>
            <div className="flex items-center text-slate-700">
              <CheckCircle2 className="w-5 h-5 mr-3 text-purple-500" />
              <span><strong>Instant Feedback</strong> - See rationale immediately</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Target className="w-5 h-5 mr-3 text-purple-500" />
              <span><strong>Focus on Weak Areas</strong> - Study by category</span>
            </div>
          </div>

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Select a Category to Study
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full p-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:border-purple-400 bg-white"
              data-testid="category-select"
            >
              <option value="">-- Choose a Category --</option>
              {categories.map((cat) => (
                <option key={cat.name} value={cat.name}>
                  {cat.name} ({cat.question_count} questions)
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleStartStudy}
            disabled={isLoading || !selectedCategory}
            className="w-full py-4 rounded-xl font-bold text-white text-lg transition-all bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            data-testid="start-study-btn"
          >
            {isLoading ? 'Starting...' : 'Start Studying'}
          </button>

          {/* Weak Areas Suggestion */}
          {stats && stats.category_performance && Object.keys(stats.category_performance).length > 0 && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-purple-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1 text-amber-500" />
                Suggested Focus Areas
              </h3>
              <div className="space-y-1">
                {Object.entries(stats.category_performance)
                  .filter(([_, data]) => data.percentage < 75)
                  .sort((a, b) => a[1].percentage - b[1].percentage)
                  .slice(0, 3)
                  .map(([cat, data]) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className="w-full text-left p-2 rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-between"
                    >
                      <span className="text-sm text-slate-700">{cat}</span>
                      <span className={`text-sm font-medium ${
                        data.percentage < 50 ? 'text-red-600' : 'text-amber-600'
                      }`}>
                        {data.percentage}%
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Section */}
      {stats && stats.total_tests > 0 && (
        <div className="max-w-4xl mx-auto mb-8">
          <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            Your Performance
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="text-3xl font-bold text-slate-800">{stats.total_tests}</div>
              <div className="text-sm text-slate-500">Tests Taken</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="text-3xl font-bold text-blue-600">{stats.average_score}%</div>
              <div className="text-sm text-slate-500">Average Score</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="text-3xl font-bold text-emerald-600">{stats.pass_rate}%</div>
              <div className="text-sm text-slate-500">Pass Rate</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <div className="text-3xl font-bold text-purple-600">{stats.lpn_tests + stats.rn_tests}</div>
              <div className="text-sm text-slate-500">LPN: {stats.lpn_tests} | RN: {stats.rn_tests}</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent History */}
      {testHistory.length > 0 && (
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center">
            <History className="w-5 h-5 mr-2" />
            Recent Tests
          </h3>
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Score</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {testHistory.slice(0, 5).map((test) => (
                  <tr key={test.test_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {new Date(test.started_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        test.exam_type === 'lpn' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {test.exam_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">
                      {test.score !== null ? `${test.score}%` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {test.status === 'completed' ? (
                        test.passed ? (
                          <span className="flex items-center text-emerald-600 text-sm">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Passed
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600 text-sm">
                            <XCircle className="w-4 h-4 mr-1" /> Failed
                          </span>
                        )
                      ) : (
                        <span className="text-amber-600 text-sm">{test.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Render test view
  const renderTest = () => {
    if (!currentQuestion) return null;

    const isSATA = currentQuestion.type === 'sata';
    const currentAnswer = answers[currentQuestion.id];
    const answeredCount = Object.keys(answers).length;
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-slate-100">
        {/* Header Bar */}
        <div className={`sticky top-0 z-10 shadow-md ${
          selectedExamType === 'lpn' ? 'bg-blue-600' : 'bg-emerald-600'
        }`}>
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  if (window.confirm('Leaving now will end this test and score it with your current answers. Unanswered questions count as incorrect. Leave the test?')) {
                    handleSubmitTest();
                  }
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors"
                data-testid="test-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-white font-semibold">
                {selectedExamType.toUpperCase()} Practice Test
              </span>
              <span className="text-white/80 text-sm">
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Timer */}
              <div className={`flex items-center px-4 py-2 rounded-lg ${
                timeRemaining < 300 ? 'bg-red-500' : 'bg-white/20'
              }`}>
                <Clock className="w-5 h-5 text-white mr-2" />
                <span className="text-white font-mono font-bold text-lg">
                  {formatTime(timeRemaining)}
                </span>
              </div>
              
              {/* Pause/Resume */}
              <button
                onClick={isPaused ? handleResumeTest : handlePauseTest}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                data-testid="pause-resume-btn"
              >
                {isPaused ? (
                  <Play className="w-5 h-5 text-white" />
                ) : (
                  <Pause className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="h-1 bg-white/20">
            <div 
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Pause Overlay */}
        {isPaused && (
          <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md text-center">
              <Pause className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Test Paused</h2>
              <p className="text-slate-600 mb-6">
                Time remaining: {formatTime(timeRemaining)}
              </p>
              <button
                onClick={handleResumeTest}
                className={`w-full py-3 rounded-xl font-semibold text-white ${
                  selectedExamType === 'lpn' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                Resume Test
              </button>
            </div>
          </div>
        )}

        {/* Question Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            {/* Category Badge */}
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-slate-100 rounded-full text-sm font-medium text-slate-600">
                {currentQuestion.category}
              </span>
              {isSATA && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  Select All That Apply
                </span>
              )}
            </div>

            {/* Question */}
            <h2 className="text-xl font-medium text-slate-800 mb-6 leading-relaxed">
              {currentQuestion.question}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const optionLetter = option.charAt(0);
                const isSelected = isSATA 
                  ? (currentAnswer || []).includes(optionLetter)
                  : currentAnswer === optionLetter;

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQuestion.id, optionLetter, isSATA)}
                    className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                      isSelected
                        ? selectedExamType === 'lpn'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`option-${optionLetter}`}
                  >
                    <div className="flex items-start">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${
                        isSelected
                          ? selectedExamType === 'lpn'
                            ? 'bg-blue-500 text-white'
                            : 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {isSATA ? (
                          isSelected ? <CheckCircle2 className="w-5 h-5" /> : optionLetter
                        ) : (
                          optionLetter
                        )}
                      </div>
                      <span className="text-slate-700 pt-1">{option.substring(3)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-800 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </button>

            <div className="text-sm text-slate-500">
              {answeredCount} of {questions.length} answered
            </div>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={handleSubmitTest}
                disabled={isLoading}
                className={`px-6 py-2 rounded-xl font-semibold text-white ${
                  selectedExamType === 'lpn' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                data-testid="submit-test-btn"
              >
                {isLoading ? 'Submitting...' : 'Submit Test'}
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </button>
            )}
          </div>

          {/* Question Navigator */}
          <div className="mt-8 bg-white rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-600 mb-3">Question Navigator</h3>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, idx) => {
                const isAnswered = answers[q.id] !== undefined;
                const isCurrent = idx === currentQuestionIndex;
                
                return (
                  <button
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                      isCurrent
                        ? selectedExamType === 'lpn'
                          ? 'bg-blue-600 text-white'
                          : 'bg-emerald-600 text-white'
                        : isAnswered
                          ? 'bg-slate-200 text-slate-700'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render results view
  const renderResults = () => {
    if (!results) return null;

    const passed = results.passed;

    return (
      <div className="min-h-screen bg-slate-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Score Card */}
          <div className={`rounded-2xl p-8 mb-8 text-center ${
            passed ? 'bg-emerald-500' : 'bg-red-500'
          }`}>
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              {passed ? (
                <Trophy className="w-12 h-12 text-white" />
              ) : (
                <AlertCircle className="w-12 h-12 text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {passed ? 'Congratulations!' : 'Keep Practicing!'}
            </h1>
            <p className="text-white/90 text-lg mb-4">
              {passed ? 'You passed the practice test!' : 'You did not meet the passing score this time.'}
            </p>
            <div className="text-6xl font-bold text-white mb-2">
              {results.percentage}%
            </div>
            <p className="text-white/80">
              {results.correct_answers} of {results.total_questions} correct
            </p>
          </div>

          {/* Category Breakdown */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Performance by Category</h2>
            <div className="space-y-4">
              {Object.entries(results.category_breakdown).map(([category, data]) => (
                <div key={category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700">{category}</span>
                    <span className="font-medium">
                      {data.correct}/{data.total} ({data.percentage}%)
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        data.percentage >= 75 ? 'bg-emerald-500' : data.percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${data.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Incorrect Questions Review */}
          {results.detailed_results.filter(r => !r.is_correct).length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                Review Incorrect Answers ({results.detailed_results.filter(r => !r.is_correct).length})
              </h2>
              <div className="space-y-6">
                {results.detailed_results.filter(r => !r.is_correct).map((result, idx) => (
                  <div key={idx} className="border-b border-slate-200 pb-6 last:border-0">
                    <div className="flex items-start space-x-3 mb-3">
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                      <div>
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                          {result.category}
                        </span>
                        <p className="text-slate-800 mt-2 font-medium">{result.question}</p>
                      </div>
                    </div>
                    
                    <div className="ml-8 space-y-2">
                      <div className="text-sm">
                        <span className="text-slate-500">Your answer: </span>
                        <span className="text-red-600 font-medium">
                          {Array.isArray(result.user_answer) 
                            ? result.user_answer.join(', ') || 'No answer'
                            : result.user_answer || 'No answer'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">Correct answer: </span>
                        <span className="text-emerald-600 font-medium">
                          {Array.isArray(result.correct_answer)
                            ? result.correct_answer.join(', ')
                            : result.correct_answer}
                        </span>
                      </div>
                      {result.rationale && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Rationale:</strong> {result.rationale}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => {
                setResults(null);
                setView('home');
              }}
              className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300"
            >
              Back to Home
            </button>
            <button
              onClick={handleStartTest}
              className={`px-6 py-3 rounded-xl font-semibold text-white ${
                selectedExamType === 'lpn' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              Take Another Test
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Study Mode view
  const renderStudy = () => {
    if (!currentQuestion) return null;

    const isSATA = currentQuestion.type === 'sata';
    const currentAnswer = answers[currentQuestion.id];
    const hasChecked = studyFeedback !== null;
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100" data-testid="study-mode-view">
        {/* Header Bar */}
        <div className="sticky top-0 z-10 shadow-md bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setView('home')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors"
                data-testid="study-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <Brain className="w-6 h-6 text-white" />
              <span className="text-white font-semibold">
                Study Mode - {selectedExamType.toUpperCase()}
              </span>
              <span className="text-white/80 text-sm">
                {currentQuestion.category}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* No timer - just progress */}
              <div className="flex items-center px-4 py-2 bg-white/20 rounded-lg">
                <BookOpen className="w-5 h-5 text-white mr-2" />
                <span className="text-white font-medium">
                  {currentQuestionIndex + 1} / {questions.length}
                </span>
              </div>
              
              {/* Exit Study */}
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to exit study mode?')) {
                    setView('home');
                    setQuestions([]);
                    setAnswers({});
                    setStudyFeedback(null);
                    setStudyResults({});
                  }
                }}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                data-testid="exit-study-btn"
              >
                <XCircle className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="h-1 bg-white/20">
            <div 
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-testid="study-question">
            {/* Category & Type Badges */}
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-purple-100 rounded-full text-sm font-medium text-purple-600">
                {currentQuestion.category}
              </span>
              <div className="flex items-center space-x-2">
                {currentQuestion.difficulty && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    currentQuestion.difficulty === 'hard' 
                      ? 'bg-red-100 text-red-700'
                      : currentQuestion.difficulty === 'medium'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-green-100 text-green-700'
                  }`}>
                    {currentQuestion.difficulty.charAt(0).toUpperCase() + currentQuestion.difficulty.slice(1)}
                  </span>
                )}
                {isSATA && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    Select All That Apply
                  </span>
                )}
              </div>
            </div>

            {/* Question */}
            <h2 className="text-xl font-medium text-slate-800 mb-6 leading-relaxed">
              {currentQuestion.question}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const optionLetter = option.charAt(0);
                const isSelected = isSATA 
                  ? (currentAnswer || []).includes(optionLetter)
                  : currentAnswer === optionLetter;
                
                // Check if this option is correct/incorrect after feedback
                let optionStyle = 'border-slate-200 hover:border-slate-300 hover:bg-slate-50';
                if (hasChecked) {
                  const correctAnswer = studyFeedback.correct_answer;
                  const isCorrect = Array.isArray(correctAnswer) 
                    ? correctAnswer.includes(optionLetter)
                    : correctAnswer === optionLetter;
                  
                  if (isCorrect) {
                    optionStyle = 'border-emerald-500 bg-emerald-50';
                  } else if (isSelected && !isCorrect) {
                    optionStyle = 'border-red-500 bg-red-50';
                  } else {
                    optionStyle = 'border-slate-200 bg-slate-50 opacity-60';
                  }
                } else if (isSelected) {
                  optionStyle = 'border-purple-500 bg-purple-50';
                }

                return (
                  <button
                    key={index}
                    onClick={() => !hasChecked && handleAnswerSelect(currentQuestion.id, optionLetter, isSATA)}
                    disabled={hasChecked}
                    className={`w-full p-4 rounded-xl text-left transition-all border-2 ${optionStyle} ${
                      hasChecked ? 'cursor-default' : 'cursor-pointer'
                    }`}
                    data-testid={`study-option-${optionLetter}`}
                  >
                    <div className="flex items-start">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${
                        hasChecked
                          ? (Array.isArray(studyFeedback.correct_answer) 
                              ? studyFeedback.correct_answer.includes(optionLetter)
                              : studyFeedback.correct_answer === optionLetter)
                            ? 'bg-emerald-500 text-white'
                            : isSelected
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-200 text-slate-600'
                          : isSelected
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                      }`}>
                        {hasChecked ? (
                          (Array.isArray(studyFeedback.correct_answer) 
                            ? studyFeedback.correct_answer.includes(optionLetter)
                            : studyFeedback.correct_answer === optionLetter)
                            ? <CheckCircle2 className="w-5 h-5" />
                            : isSelected
                              ? <XCircle className="w-5 h-5" />
                              : optionLetter
                        ) : (
                          isSATA && isSelected ? <CheckCircle2 className="w-5 h-5" /> : optionLetter
                        )}
                      </div>
                      <span className="text-slate-700 pt-1">
                        {hasChecked && <span className="font-semibold mr-1">{optionLetter}.</span>}
                        {option.substring(3)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feedback Panel */}
          {hasChecked && (
            <div 
              className={`rounded-2xl shadow-lg p-6 mb-6 ${
                studyFeedback.is_correct ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-red-50 border-2 border-red-200'
              }`}
              data-testid="study-feedback"
            >
              <div className="flex items-center mb-4">
                {studyFeedback.is_correct ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-emerald-600 mr-3" />
                    <span className="text-xl font-bold text-emerald-700">Correct!</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-8 h-8 text-red-600 mr-3" />
                    <span className="text-xl font-bold text-red-700">Incorrect</span>
                  </>
                )}
              </div>
              
              {!studyFeedback.is_correct && (
                <div className="mb-4 p-3 bg-white rounded-lg">
                  <span className="text-slate-600">Correct answer: </span>
                  <span className="font-semibold text-emerald-700">
                    {Array.isArray(studyFeedback.correct_answer)
                      ? studyFeedback.correct_answer.join(', ')
                      : studyFeedback.correct_answer}
                  </span>
                </div>
              )}
              
              {studyFeedback.rationale && (
                <div className="p-4 bg-white rounded-lg">
                  <div className="flex items-start">
                    <Lightbulb className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Rationale</h4>
                      <p className="text-slate-700 leading-relaxed">{studyFeedback.rationale}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setStudyFeedback(null);
                setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
              }}
              disabled={currentQuestionIndex === 0}
              className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-800 disabled:opacity-50"
              data-testid="prev-study-question-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </button>

            <div className="text-sm text-slate-500">
              {Object.keys(studyResults).length} of {questions.length} reviewed
            </div>

            <div className="flex items-center space-x-3">
              {!hasChecked ? (
                <button
                  onClick={handleCheckStudyAnswer}
                  disabled={!currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0) || isLoading}
                  className="px-6 py-2 rounded-xl font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="check-answer-btn"
                >
                  {isLoading ? 'Checking...' : 'Check Answer'}
                </button>
              ) : isLastQuestion ? (
                <button
                  onClick={handleCompleteStudy}
                  disabled={isLoading}
                  className="px-6 py-2 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  data-testid="complete-study-btn"
                >
                  {isLoading ? 'Completing...' : 'Complete Session'}
                </button>
              ) : (
                <button
                  onClick={handleNextStudyQuestion}
                  className="flex items-center px-6 py-2 rounded-xl font-semibold text-white bg-purple-600 hover:bg-purple-700"
                  data-testid="next-question-btn"
                >
                  Next Question
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Study Results view
  const renderStudyResults = () => {
    const totalQuestions = Object.keys(studyResults).length;
    const correctAnswers = Object.values(studyResults).filter(r => r.is_correct).length;
    const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-12" data-testid="study-results-view">
        <div className="max-w-3xl mx-auto px-4">
          {/* Results Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-8 text-center">
              <Brain className="w-16 h-16 text-white/80 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Study Session Complete!</h1>
              <p className="text-white/80">Great job practicing your knowledge</p>
            </div>
            
            <div className="p-8">
              {/* Score Circle */}
              <div className="flex justify-center mb-8">
                <div className={`w-40 h-40 rounded-full flex items-center justify-center border-8 ${
                  percentage >= 80 ? 'border-emerald-500 bg-emerald-50' :
                  percentage >= 60 ? 'border-amber-500 bg-amber-50' :
                  'border-red-500 bg-red-50'
                }`}>
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${
                      percentage >= 80 ? 'text-emerald-600' :
                      percentage >= 60 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {percentage}%
                    </div>
                    <div className="text-slate-500 text-sm">Score</div>
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-2xl font-bold text-slate-800">{totalQuestions}</div>
                  <div className="text-sm text-slate-500">Questions</div>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <div className="text-2xl font-bold text-emerald-600">{correctAnswers}</div>
                  <div className="text-sm text-emerald-600">Correct</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <div className="text-2xl font-bold text-red-600">{totalQuestions - correctAnswers}</div>
                  <div className="text-sm text-red-600">Incorrect</div>
                </div>
              </div>
              
              {/* Performance Message */}
              <div className={`p-4 rounded-xl text-center mb-6 ${
                percentage >= 80 ? 'bg-emerald-100 text-emerald-800' :
                percentage >= 60 ? 'bg-amber-100 text-amber-800' :
                'bg-red-100 text-red-800'
              }`}>
                {percentage >= 80 ? (
                  <p className="font-medium">🌟 Excellent work! You're mastering this material!</p>
                ) : percentage >= 60 ? (
                  <p className="font-medium">📚 Good progress! Keep practicing to improve.</p>
                ) : (
                  <p className="font-medium">💪 Keep studying! Review the rationales for areas to focus on.</p>
                )}
              </div>
              
              {/* Review Incorrect Answers */}
              {Object.values(studyResults).some(r => !r.is_correct) && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 text-amber-500 mr-2" />
                    Review These Questions
                  </h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {Object.entries(studyResults)
                      .filter(([_, r]) => !r.is_correct)
                      .map(([qId, result], idx) => {
                        const question = questions.find(q => q.id === qId);
                        return (
                          <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-slate-800 font-medium mb-2">{question?.question}</p>
                            <div className="text-sm space-y-1">
                              <p><span className="text-slate-500">Your answer:</span> <span className="text-red-600 font-medium">{
                                Array.isArray(result.user_answer) 
                                  ? result.user_answer.join(', ') || 'No answer'
                                  : result.user_answer || 'No answer'
                              }</span></p>
                              <p><span className="text-slate-500">Correct:</span> <span className="text-emerald-600 font-medium">{
                                Array.isArray(result.correct_answer)
                                  ? result.correct_answer.join(', ')
                                  : result.correct_answer
                              }</span></p>
                            </div>
                            {result.rationale && (
                              <p className="text-sm text-slate-600 mt-2 italic">"{result.rationale}"</p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => {
                setResults(null);
                setStudyResults({});
                setQuestions([]);
                setAnswers({});
                setStudyFeedback(null);
                setView('home');
              }}
              className="px-6 py-3 bg-white text-slate-700 rounded-xl font-semibold hover:bg-slate-50 shadow-md"
              data-testid="back-to-home-btn"
            >
              Back to Home
            </button>
            <button
              onClick={() => {
                setStudyResults({});
                setAnswers({});
                setStudyFeedback(null);
                setCurrentQuestionIndex(0);
                setView('home');
              }}
              className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md"
              data-testid="study-again-btn"
            >
              Study Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="min-h-screen bg-slate-50">
      {view === 'home' && renderHome()}
      {view === 'test' && renderTest()}
      {view === 'results' && renderResults()}
      {view === 'study' && renderStudy()}
      {view === 'study-results' && renderStudyResults()}
    </div>
  );
};

export default NCLEXPractice;
